/**
 * Age decryption in the browser
 * 
 * The decryption key is extracted from the URL fragment, which is NEVER
 * sent to the server. This ensures true end-to-end encryption.
 */

import { chacha20poly1305 } from '@noble/ciphers/chacha.js'
import { x25519 } from '@noble/curves/ed25519.js'

/**
 * Parse the decryption key from URL fragment
 * Format: base64url encoded 32-byte X25519 private key
 */
export function parseKeyFromFragment(): Uint8Array | null {
  const fragment = window.location.hash.slice(1) // Remove leading #
  
  if (!fragment) {
    return null
  }

  try {
    // Convert base64url to standard base64
    let base64 = fragment.replace(/-/g, '+').replace(/_/g, '/')
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '='
    }

    // Decode base64
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    // Validate key length (32 bytes for X25519)
    if (bytes.length !== 32) {
      console.error('Invalid key length:', bytes.length)
      return null
    }

    return bytes
  } catch (error) {
    console.error('Failed to parse key from fragment:', error)
    return null
  }
}

/**
 * HKDF using Web Crypto API
 */
async function hkdf(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    ikm as unknown as BufferSource,
    'HKDF',
    false,
    ['deriveBits']
  )

  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt as unknown as BufferSource,
      info: info as unknown as BufferSource,
    },
    key,
    length * 8
  )

  return new Uint8Array(derived)
}

/**
 * Decrypt an age-encrypted file
 * 
 * Age file format:
 * - Header: age-encryption.org/v1\n
 * - Recipients: -> X25519 <ephemeral-share>\n<wrapped-key>\n
 * - End of header: ---\n
 * - Payload: ChaCha20-Poly1305 encrypted in 64KB chunks
 */
export async function decryptAge(
  encryptedData: ArrayBuffer,
  privateKey: Uint8Array
): Promise<ArrayBuffer> {
  const data = new Uint8Array(encryptedData)
  const textDecoder = new TextDecoder()

  // Find the header end marker "--- " followed by MAC and newline
  // Format: "--- <base64-MAC>\n"
  let headerEnd = -1
  for (let i = 0; i < data.length - 4; i++) {
    // Look for "--- " (dash dash dash space)
    if (data[i] === 0x2d && data[i + 1] === 0x2d && data[i + 2] === 0x2d && data[i + 3] === 0x20) {
      // Find the newline after the MAC
      for (let j = i + 4; j < data.length; j++) {
        if (data[j] === 0x0a) {
          headerEnd = j + 1
          break
        }
      }
      break
    }
  }

  if (headerEnd === -1) {
    throw new Error('Invalid age file: header end marker not found')
  }

  // Parse header
  const headerText = textDecoder.decode(data.slice(0, headerEnd))
  const lines = headerText.split('\n')

  // Validate header version
  if (!lines[0].startsWith('age-encryption.org/v1')) {
    throw new Error('Invalid age file: unsupported version')
  }

  // Parse X25519 recipient stanza
  let ephemeralShareB64 = ''
  let wrappedKeyB64 = ''
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('-> X25519 ')) {
      ephemeralShareB64 = line.slice('-> X25519 '.length).trim()
      // Next non-empty, non-marker line is the wrapped key
      for (let j = i + 1; j < lines.length; j++) {
        const nextLine = lines[j].trim()
        if (nextLine && nextLine !== '---') {
          wrappedKeyB64 = nextLine
          break
        }
      }
      break
    }
  }

  if (!ephemeralShareB64 || !wrappedKeyB64) {
    throw new Error('Invalid age file: X25519 recipient not found')
  }

  // Decode base64 values
  const ephemeralShare = base64Decode(ephemeralShareB64)
  const wrappedKey = base64Decode(wrappedKeyB64)

  // Perform X25519 key exchange: shared_secret = X25519(our_private, their_public)
  const sharedSecret = x25519.getSharedSecret(privateKey, ephemeralShare)

  // Get our public key for the salt
  const recipientPublic = x25519.getPublicKey(privateKey)

  // Derive wrap key using HKDF
  // Salt is: ephemeral_share || recipient_public_key
  // Info is: "age-encryption.org/v1/X25519"
  const salt = new Uint8Array(ephemeralShare.length + recipientPublic.length)
  salt.set(ephemeralShare, 0)
  salt.set(recipientPublic, ephemeralShare.length)
  
  const info = new TextEncoder().encode('age-encryption.org/v1/X25519')
  
  const wrapKey = await hkdf(
    sharedSecret,
    salt,
    info,
    32
  )

  // Unwrap the file key using ChaCha20-Poly1305 with zero nonce
  const zeroNonce = new Uint8Array(12)
  const chacha = chacha20poly1305(wrapKey, zeroNonce)
  const fileKey = chacha.decrypt(wrappedKey)

  // Get the payload (everything after header)
  const payloadWithNonce = data.slice(headerEnd)
  
  // The payload starts with a 16-byte nonce, then the encrypted chunks
  const payloadNonce = payloadWithNonce.slice(0, 16)
  const payload = payloadWithNonce.slice(16)

  // Derive payload key using HKDF
  // Salt is the 16-byte payload nonce, info is "payload"
  const payloadInfo = new TextEncoder().encode('payload')
  const payloadKey = await hkdf(
    fileKey,
    payloadNonce,
    payloadInfo,
    32
  )

  // Decrypt payload chunks
  // Age uses 64KB chunks, each chunk is ciphertext + 16-byte Poly1305 tag
  const CHUNK_SIZE = 64 * 1024 // 64KB of plaintext per chunk
  const chunks: Uint8Array[] = []
  let offset = 0
  let chunkIndex = 0

  while (offset < payload.length) {
    // Calculate chunk size (ciphertext = plaintext + 16 byte tag)
    const remainingBytes = payload.length - offset
    const isLastChunk = remainingBytes <= CHUNK_SIZE + 16
    const chunkCiphertextSize = isLastChunk ? remainingBytes : CHUNK_SIZE + 16

    const chunkCiphertext = payload.slice(offset, offset + chunkCiphertextSize)
    
    // Construct nonce: 11-byte big-endian counter + 1-byte last chunk flag
    // The counter is big-endian and occupies bytes 0-10
    const nonce = new Uint8Array(12)
    // For practical purposes, chunk index fits in a 32-bit number
    // Write it at the end of the 11-byte counter (bytes 7-10)
    nonce[7] = (chunkIndex >>> 24) & 0xff
    nonce[8] = (chunkIndex >>> 16) & 0xff
    nonce[9] = (chunkIndex >>> 8) & 0xff
    nonce[10] = chunkIndex & 0xff
    // Byte 11: last chunk flag (0x01 for final chunk, 0x00 otherwise)
    nonce[11] = isLastChunk ? 0x01 : 0x00

    const chunkChacha = chacha20poly1305(payloadKey, nonce)
    const decrypted = chunkChacha.decrypt(chunkCiphertext)
    chunks.push(decrypted)

    offset += chunkCiphertextSize
    chunkIndex++
  }

  // Combine all chunks
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const result = new Uint8Array(totalLength)
  let resultOffset = 0
  for (const chunk of chunks) {
    result.set(chunk, resultOffset)
    resultOffset += chunk.length
  }

  return result.buffer
}

/**
 * Decode standard base64 (not base64url)
 */
function base64Decode(input: string): Uint8Array {
  const binary = atob(input)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Trigger a file download in the browser
 */
export function downloadFile(data: ArrayBuffer, filename: string): void {
  const blob = new Blob([data])
  const url = URL.createObjectURL(blob)
  
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  
  document.body.appendChild(a)
  a.click()
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 100)
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)
  
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}
